import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, tap, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  LocationInventorySummary,
  DirectUpdateRequest,
  TransferRequest,
  PendingTransfer,
  PendingTransfersResponse,
  ConfirmTransferRequest,
  InventoryApiResponse,
  InventorySummaryResponse,
  MapOverviewResponse,
  ForecastResponse
} from '../models';

/**
 * MatInventoryService
 * 
 * Handles all inventory management API calls to the .NET Lambda backend.
 * The MSAL interceptor automatically adds the Authorization header for all
 * requests to the configured API base URL.
 * 
 * State Management:
 * - Maintains a BehaviorSubject for summary data to enable automatic refresh
 * - All POST operations trigger a refresh of the summary data
 */
@Injectable({
  providedIn: 'root'
})
export class MatInventoryService {
  private readonly baseUrl = environment.apiBaseUrl;
  
  // State management for full summary response
  private summaryResponseSubject = new BehaviorSubject<InventorySummaryResponse | null>(null);
  public summaryResponse$ = this.summaryResponseSubject.asObservable();
  
  // State management for summary data (legacy)
  private summarySubject = new BehaviorSubject<LocationInventorySummary[]>([]);
  public summary$ = this.summarySubject.asObservable();

  // State management for pending transfers
  private pendingTransfersSubject = new BehaviorSubject<PendingTransfer[]>([]);
  public pendingTransfers$ = this.pendingTransfersSubject.asObservable();

  constructor(private http: HttpClient) {}

  /**
   * GET /getsummary
   * Retrieves complete inventory summary including KPIs, storage hubs, and location summaries
   */
  getSummary(): Observable<InventorySummaryResponse> {
    return this.http.get<InventorySummaryResponse>(`${this.baseUrl}/getsummary`)
      .pipe(
        tap(data => {
          this.summaryResponseSubject.next(data);
          // For backwards compatibility, also update the legacy subject
          // Note: You'll need to map LocationMaterialSummaries to LocationInventorySummary if needed
        }),
        catchError(this.handleError)
      );
  }

  /**
   * GET /getMapOverview
   * Retrieves map-specific data optimized for map visualization
   * Includes location coordinates, inventory levels, and map markers
   */
  getMapOverview(): Observable<MapOverviewResponse> {
    return this.http.get<MapOverviewResponse>(`${this.baseUrl}/getMapOverview`)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * GET /getForecasting
   * Retrieves demand forecast data for specified region and time horizon
   * @param region Region filter (ALL, DEC, DEF, DEM, DEP) - default: ALL
   * @param weeks Forecast horizon (4, 8, or 12 weeks) - default: 12
   */
  getForecasting(region: string = 'ALL', weeks: number = 12): Observable<ForecastResponse> {
    return this.http.get<ForecastResponse>(`${this.baseUrl}/getForecasting`, {
      params: { region, weeks: weeks.toString() }
    })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * POST /directupdate
   * Performs manual inventory adjustments (Add or Remove)
   * Automatically refreshes summary data after successful update
   */
  directUpdate(request: DirectUpdateRequest): Observable<any> {
    return this.http.post(`${this.baseUrl}/directupdate`, request)
      .pipe(
        tap(() => this.refreshSummary()),
        catchError(this.handleError)
      );
  }

  /**
   * POST /requesttransfer
   * Initiates a transfer from source to target location
   * Moves inventory from Source-Available to Source-In_Transit
   * Automatically refreshes summary data after successful transfer
   */
  requestTransfer(request: TransferRequest): Observable<any> {
    return this.http.post(`${this.baseUrl}/requesttransfer`, request)
      .pipe(
        tap(() => {
          this.refreshSummary();
          this.refreshPendingTransfers();
        }),
        catchError(this.handleError)
      );
  }

  /**
   * GET /getpendingtransfers
   * Retrieves all transfers with EventStatusCd == 'Initiated'
   */
  getPendingTransfers(): Observable<PendingTransfer[]> {
    return this.http.get<PendingTransfersResponse>(`${this.baseUrl}/getpendingtransfers`)
      .pipe(
        map(response => response.Transfers || []),
        tap(transfers => this.pendingTransfersSubject.next(transfers)),
        catchError(this.handleError)
      );
  }

  /**
   * POST /confirmtransfer
   * Finalizes a transfer by moving inventory to Target-Available bucket
   * Automatically refreshes both summary and pending transfers after confirmation
   */
  confirmTransfer(request: ConfirmTransferRequest): Observable<any> {
    return this.http.post(`${this.baseUrl}/confirmtransfer`, request)
      .pipe(
        tap(() => {
          this.refreshSummary();
          this.refreshPendingTransfers();
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Refresh summary data (called internally after POST operations)
   */
  private refreshSummary(): void {
    this.getSummary().subscribe();
  }

  /**
   * Refresh pending transfers data (called internally after POST operations)
   */
  private refreshPendingTransfers(): void {
    this.getPendingTransfers().subscribe();
  }

  /**
   * Error handler for HTTP requests
   */
  private handleError(error: any): Observable<never> {
    console.error('Inventory API Error:', error);
    
    let errorMessage = 'An error occurred while processing your request.';
    
    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // Server-side error
      errorMessage = error.error?.message || 
                     error.message || 
                     `Server returned code ${error.status}`;
    }
    
    return throwError(() => new Error(errorMessage));
  }
}
